import { mod } from "./MathUtil";

/**
 * Utility functions for doing things based on random numbers.
 */

/** Shorthand for Math.random */
const r = Math.random;

/** Return a random number between `min` and `max`. */
export function rUniform(min: number, max: number): number {
  if (min == null) {
    return r();
  }
  if (max == null) {
    max = min;
    min = 0;
  }
  return (max - min) * r() + min;
}

/** A random angle in radians */
export function rDirection(): number {
  return rUniform(0, Math.PI * 2);
}

/** One of the four cardinal directions, in radians */
export function rCardinal(): number {
  return choose(0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2);
}

/**
 * Returns a random number from an (approximately) normal distribution
 * centered at `mean` with `deviation`
 */
export function rNormal(mean: number = 0.0, deviation: number = 1.0): number {
  return (deviation * (r() + r() + r() + r() + r() + r() - 3)) / 3 + mean;
}

/** Return true or false, chosen at random. */
export function rBool(chanceOfTrue: number = 0.5): boolean {
  return r() < chanceOfTrue;
}

/** Returns a random sign (-1 or 1) */
export function rSign(chanceOfPositive: number = 0.5): -1 | 1 {
  return rBool(chanceOfPositive) ? 1 : -1;
}

/** Return a random integer in range [min, max) */
export function rInteger(min: number, max: number): number {
  return Math.floor(rUniform(min, max));
}

/** Returns a random byte value (0-255) */
export function rByte(): number {
  return rInteger(0, 256);
}

/**
 * Probabilistically round x to a nearby integer.
 */
export function rRound(x: number): number {
  const low = Math.floor(x);
  return rBool(x - low) ? low + 1 : low;
}

/** Return a random element from an array. */
export function choose<T>(...options: T[]): T {
  return options[rInteger(0, options.length)];
}

/**
 * Return N random elements from an array without replacement.
 * Each element can only be selected once.
 * @param n Number of elements to select
 * @param options Array to select from
 * @returns Array of n randomly selected unique elements
 */
export function chooseN<T>(n: number, options: T[]): T[] {
  if (n < 0) {
    throw new Error("Cannot choose negative number of elements");
  }
  if (options.length === 0) {
    throw new Error("Cannot choose from empty array");
  }
  if (n > options.length) {
    throw new Error(
      `Cannot choose ${n} unique elements from array of length ${options.length}`,
    );
  }

  // For small n relative to array size, use selection algorithm
  if (n <= options.length / 3) {
    const selected = new Set<number>();
    const result: T[] = [];
    while (selected.size < n) {
      const idx = rInteger(0, options.length);
      if (!selected.has(idx)) {
        selected.add(idx);
        result.push(options[idx]);
      }
    }
    return result;
  }

  // For larger n, shuffle and take first n elements
  const indices = Array.from({ length: options.length }, (_, i) => i);
  shuffle(indices);
  return indices.slice(0, n).map((i) => options[i]);
}

/**
 * Choose a random element based on weights.
 * Higher weights are more likely to be selected.
 * @param options Array of [item, weight] tuples
 * @returns The selected item
 */
export function chooseWeighted<T>(
  options: ReadonlyArray<Readonly<[T, number]>>,
): T {
  if (options.length === 0) {
    throw new Error("Cannot choose from empty array");
  }

  let totalWeight = 0;
  for (const [_, weight] of options) {
    if (weight < 0) {
      throw new Error("Weights must be non-negative");
    }
    totalWeight += weight;
  }

  if (totalWeight === 0) {
    throw new Error("Total weight must be greater than 0");
  }

  let random = rUniform(0, totalWeight);
  for (const [item, weight] of options) {
    random -= weight;
    if (random <= 0) {
      return item;
    }
  }

  // Fallback (should never happen)
  return options[options.length - 1][0];
}

/** Remove and return a random element from an array. */
export function take<T>(options: T[]): T {
  if (options.length === 0) {
    throw new Error("Cannot take from empty array");
  }
  return options.splice(rInteger(0, options.length), 1)[0];
}

/** Put an array into a random order and return the array. */
export function shuffle<T>(a: T[]): T[] {
  let i, j;
  i = a.length;
  while (--i > 0) {
    j = rInteger(0, i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Put an array into a deterministically random order and return the array.
 * Uses a linear congruential generator for deterministic randomness.
 * @param a The array to shuffle
 * @param seed Should be an integer for deterministic results
 */
export function seededShuffle<T>(a: T[], seed: number): T[] {
  let i, j, temp;
  i = a.length;
  while (--i > 0) {
    seed = (seed * 1103515245 + 12345) | 0;
    j = mod(seed, i + 1);
    temp = a[j];
    a[j] = a[i];
    a[i] = temp;
  }
  return a;
}
